import { motion } from 'framer-motion';
import { Helmet } from 'react-helmet-async';
import { Link } from 'react-router-dom';
import {
  Phone, ArrowRight, Star, MapPin, BookOpen, Users,
  GraduationCap, Trophy, CheckCircle2, Clock, Target, Zap
} from 'lucide-react';
import {
  Accordion, AccordionContent, AccordionItem, AccordionTrigger
} from '@/components/ui/accordion';
import LeadForm from '@/components/LeadForm';

const WA = 'https://wa.me/919930010800?text=Hi%20Planet%20Gyaan!%20I%27m%20looking%20for%20the%20best%20CBSE%20coaching%20in%20Santacruz.';
const TEL = 'tel:+919930010800';
const LOGO = 'https://customer-assets.emergentagent.com/job_planet-gyaan-edu/artifacts/no8w3qt3_Untitled%20design-73.png';

const WaIcon = ({ className = "w-5 h-5" }) => (
  <svg className={className} viewBox="0 0 24 24" fill="currentColor">
    <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/>
  </svg>
);

const reasons = [
  { icon: Trophy, title: '95%+ Board Results', desc: 'Our students consistently score 95%+ in CBSE board exams. Proven track record since 2013.' },
  { icon: Users, title: 'Small Batches Only', desc: 'Max 8-10 students per batch. Every student gets individual attention and doubt resolution.' },
  { icon: GraduationCap, title: 'Expert CBSE Faculty', desc: 'Teachers with 8-15 years of experience who understand the CBSE exam pattern inside out.' },
  { icon: Target, title: 'JEE + NEET Integrated', desc: 'Competitive exam prep built into the curriculum. No need for separate coaching.' },
  { icon: Zap, title: 'Concept-First Approach', desc: 'No rote learning. We build deep understanding that helps in boards AND competitive exams.' },
  { icon: Clock, title: 'Flexible Batch Timings', desc: 'Morning, afternoon & evening batches. Virtual + Offline options to suit your schedule.' },
];

const comparisons = [
  { feature: 'Batch Size', us: '8-10 Students', others: '30-50+ Students' },
  { feature: 'Teaching Method', us: 'Concept-Based', others: 'Rote Learning' },
  { feature: 'Doubt Clearing', us: '1:1 Private Sessions', others: 'Group Only' },
  { feature: 'Study Material', us: 'Customized for CBSE', others: 'Generic' },
  { feature: 'Performance Tracking', us: 'Weekly Reports', others: 'Monthly/None' },
  { feature: 'Career Guidance', us: 'Included Free', others: 'Extra Cost' },
];

const testimonials = [
  { name: 'Arjun S.', grade: 'Grade 12', text: 'I tried 3 different coaching centres in Santacruz before Planet Gyaan. The difference is night and day. Scored 97% in Math!', score: '97%' },
  { name: 'Priya M.', grade: 'Grade 11', text: 'The best thing about Planet Gyaan is the small batch size. I can actually ask questions without feeling embarrassed.', score: '95%' },
  { name: 'Sneha T.', grade: 'Grade 12', text: 'Planet Gyaan is the reason I cracked NEET. The integrated preparation alongside CBSE boards saved me so much time.', score: 'NEET' },
  { name: 'Rahul K.', grade: 'Grade 10', text: 'My Math scores jumped from 65% to 92% in just one term. The teachers genuinely care about each student.', score: '92%' },
];

const faqs = [
  { q: 'What makes Planet Gyaan the best CBSE coaching in Santacruz?', a: 'Planet Gyaan stands out because of our small batch sizes (8-10 students), concept-based teaching, experienced faculty, weekly assessments, and proven 95%+ results since 2013. We also integrate JEE/NEET preparation within the CBSE curriculum.' },
  { q: 'Which subjects and grades do you cover?', a: 'We offer Mathematics and Science (Physics, Chemistry, Biology) coaching for CBSE students from Grade 8 to Grade 12.' },
  { q: 'Where exactly is Planet Gyaan located in Santacruz?', a: 'We are located at Ground floor, Dastoor Store, Tirupati Shopping Center, 8, Swami Vivekanand Rd, near Riyaasat Store, opp. Kora and Phiroze, Willingdon, Santacruz (West), Mumbai.' },
  { q: 'Do you offer online CBSE coaching?', a: 'Yes! We offer both virtual (online) and offline CBSE coaching. You can choose the mode that suits you, or even switch between them.' },
  { q: 'How are your results compared to other coaching centres in Santacruz?', a: 'Our students consistently achieve 95%+ in CBSE boards, with many clearing JEE and NEET. Our small batch sizes and personalized approach deliver results that large coaching centres simply cannot match.' },
  { q: 'Is there a free demo class available?', a: 'Yes! We offer a free demo class so you can experience our teaching before enrolling. WhatsApp us at 9930010800 to book yours.' },
  { q: 'What is the fee structure?', a: 'Please contact us via WhatsApp at 9930010800 or call us for the latest fee structure. We offer competitive pricing for the quality of education provided.' },
];

const BestCBSECoaching = () => (
  <>
    <Helmet>
      <title>Best CBSE Coaching in Santacruz | #1 Math & Science Institute | Planet Gyaan</title>
      <meta name="description" content="Looking for the best CBSE coaching in Santacruz? Planet Gyaan offers top-rated Math & Science coaching for Grades 8-12. Small batches, 95%+ results, JEE & NEET prep. Since 2013. Call 9930010800." />
      <meta name="keywords" content="best CBSE coaching Santacruz, top coaching institute Santacruz, math coaching Santacruz, science coaching Santacruz Mumbai, CBSE tuition near me, best tuition classes Santacruz West, Planet Gyaan reviews" />
      <meta property="og:title" content="Best CBSE Coaching in Santacruz | Planet Gyaan" />
      <meta property="og:description" content="#1 rated CBSE Math & Science coaching in Santacruz. 95%+ results. Small batches. Since 2013." />
      <meta property="og:image" content={LOGO} />
      <meta property="og:type" content="website" />
      <link rel="canonical" href="https://planetgyaan.com/best-cbse-coaching-santacruz" />
      <script type="application/ld+json">{`
        {
          "@context": "https://schema.org",
          "@type": "EducationalOrganization",
          "name": "Planet Gyaan - Best CBSE Coaching in Santacruz",
          "description": "Top-rated CBSE Math & Science coaching institute in Santacruz, Mumbai. Small batches, 95%+ results since 2013.",
          "url": "https://planetgyaan.com/best-cbse-coaching-santacruz",
          "telephone": "+919930010800",
          "address": {
            "@type": "PostalAddress",
            "streetAddress": "Ground floor, Dastoor Store, Tirupati Shopping Center, 8, Swami Vivekanand Rd",
            "addressLocality": "Santacruz West",
            "addressRegion": "Mumbai, Maharashtra",
            "postalCode": "400054",
            "addressCountry": "IN"
          },
          "foundingDate": "2013",
          "aggregateRating": {
            "@type": "AggregateRating",
            "ratingValue": "4.9",
            "reviewCount": "150",
            "bestRating": "5"
          }
        }
      `}</script>
    </Helmet>

    {/* HERO */}
    <section data-testid="best-coaching-hero" className="relative min-h-[85vh] bg-[#18181B] flex items-center overflow-hidden noise-bg">
      {['\u2605', '#1', '\u2713', '95%', 'A+'].map((f, i) => (
        <motion.span
          key={i}
          className="absolute text-[#F2C402]/10 font-sans text-2xl font-black select-none pointer-events-none"
          style={{ left: `${12 + (i * 18) % 80}%`, top: `${18 + (i * 15) % 60}%` }}
          animate={{ y: [0, -12, 0], scale: [1, 1.1, 1] }}
          transition={{ duration: 3 + i * 0.6, repeat: Infinity, ease: 'easeInOut', delay: i * 0.3 }}
        >
          {f}
        </motion.span>
      ))}
      <div className="relative z-10 container mx-auto px-6 lg:px-16 pt-28 pb-16">
        <div className="grid lg:grid-cols-2 gap-12 items-center">
          <div>
            <motion.div
              initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}
              className="flex items-center gap-2 mb-6"
            >
              <div className="flex gap-0.5">
                {[...Array(5)].map((_, i) => <Star key={i} className="w-4 h-4 fill-[#F2C402] text-[#F2C402]" />)}
              </div>
              <span className="text-white/50 text-xs font-body">Rated #1 in Santacruz</span>
            </motion.div>
            <motion.h1
              initial={{ opacity: 0, y: 40 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.1, duration: 0.7 }}
              className="text-4xl sm:text-5xl lg:text-6xl font-black tracking-tighter leading-[0.95]"
            >
              <span className="text-[#F2C402]">Best CBSE Coaching</span><br/>
              <span className="text-white">in Santacruz</span>
            </motion.h1>
            <motion.p
              initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.2, duration: 0.6 }}
              className="text-white/60 text-base md:text-lg mt-6 max-w-lg font-body leading-relaxed"
            >
              Planet Gyaan has been the top choice for CBSE students in Santacruz since 2013. 95%+ results, small batches, expert faculty. Math & Science for Grades 8-12.
            </motion.p>
            <motion.div
              initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.3, duration: 0.6 }}
              className="flex flex-wrap gap-4 mt-8"
            >
              <a href={WA} target="_blank" rel="noopener noreferrer" data-testid="best-whatsapp-cta"
                className="flex items-center gap-2 bg-[#25D366] text-white px-6 py-3.5 rounded-xl font-bold text-sm hover:brightness-110 hover:scale-105 transition-all">
                <WaIcon /> Book Free Demo
              </a>
              <a href={TEL} data-testid="best-call-cta"
                className="flex items-center gap-2 bg-[#F2C402] text-[#0A0A0A] px-6 py-3.5 rounded-xl font-bold text-sm hover:bg-[#E9811F] hover:text-white hover:scale-105 transition-all">
                <Phone className="w-4 h-4" /> Call 9930010800
              </a>
            </motion.div>
          </div>
          <div className="hidden lg:block">
            <LeadForm variant="dark" />
          </div>
        </div>
      </div>
    </section>

    {/* WHY WE'RE THE BEST */}
    <section data-testid="best-reasons" className="py-20 md:py-28 bg-white">
      <div className="container mx-auto px-6 lg:px-16">
        <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} className="text-center mb-16">
          <h2 className="text-3xl md:text-4xl lg:text-5xl font-black text-[#121212] tracking-tight">
            Why Planet Gyaan is the <span className="text-[#F2C402]">Best Choice</span>
          </h2>
          <p className="text-[#52525B] text-base md:text-lg mt-4 font-body max-w-xl mx-auto">
            Here's what sets us apart from every other coaching centre in Santacruz.
          </p>
        </motion.div>
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
          {reasons.map((r, i) => (
            <motion.div
              key={i}
              initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }} transition={{ delay: i * 0.05, duration: 0.4 }}
              className="brutalist-card rounded-2xl p-6 bg-white"
            >
              <div className="w-10 h-10 rounded-lg bg-[#F2C402]/10 flex items-center justify-center mb-4">
                <r.icon className="w-5 h-5 text-[#F2C402]" />
              </div>
              <h3 className="font-sans font-bold text-[#121212] text-base mb-2">{r.title}</h3>
              <p className="text-[#52525B] text-sm font-body leading-relaxed">{r.desc}</p>
            </motion.div>
          ))}
        </div>
      </div>
    </section>

    {/* COMPARISON TABLE */}
    <section data-testid="best-comparison" className="py-20 md:py-28 bg-[#FAFAFA]">
      <div className="container mx-auto px-6 lg:px-16 max-w-4xl">
        <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} className="text-center mb-12">
          <h2 className="text-3xl md:text-4xl font-black text-[#121212] tracking-tight">
            Planet Gyaan vs <span className="text-[#52525B]">Other Coaching</span>
          </h2>
        </motion.div>
        <div className="brutalist-card rounded-2xl overflow-hidden bg-white">
          <div className="grid grid-cols-3 bg-[#121212] text-white text-sm font-sans font-bold">
            <div className="p-4">Feature</div>
            <div className="p-4 text-center bg-[#F2C402] text-[#0A0A0A]">Planet Gyaan</div>
            <div className="p-4 text-center">Others</div>
          </div>
          {comparisons.map((c, i) => (
            <div key={i} className={`grid grid-cols-3 text-sm font-body ${i % 2 === 0 ? 'bg-white' : 'bg-[#FAFAFA]'} border-b border-gray-100`}>
              <div className="p-4 font-medium text-[#121212]">{c.feature}</div>
              <div className="p-4 text-center text-[#121212] font-bold flex items-center justify-center gap-1">
                <CheckCircle2 className="w-4 h-4 text-[#25D366] shrink-0" /> {c.us}
              </div>
              <div className="p-4 text-center text-[#52525B]">{c.others}</div>
            </div>
          ))}
        </div>
      </div>
    </section>

    {/* TESTIMONIALS */}
    <section data-testid="best-testimonials" className="py-20 md:py-28 bg-white">
      <div className="container mx-auto px-6 lg:px-16">
        <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} className="text-center mb-16">
          <h2 className="text-3xl md:text-4xl font-black text-[#121212] tracking-tight">
            What Santacruz Students <span className="text-[#F2C402]">Say About Us</span>
          </h2>
        </motion.div>
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-5">
          {testimonials.map((t, i) => (
            <motion.div
              key={i}
              initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }} transition={{ delay: i * 0.1 }}
              data-testid={`best-testimonial-${i}`}
              className="bg-[#FAFAFA] rounded-2xl p-6 border-2 border-[#121212] shadow-[4px_4px_0px_0px_rgba(0,0,0,1)]"
            >
              <div className="flex gap-1 mb-4">
                {[...Array(5)].map((_, j) => <Star key={j} className="w-4 h-4 fill-[#F2C402] text-[#F2C402]" />)}
              </div>
              <p className="text-[#121212] text-sm font-body leading-relaxed mb-4">"{t.text}"</p>
              <div className="flex items-center justify-between pt-4 border-t border-gray-200">
                <div>
                  <span className="block font-sans font-bold text-[#121212] text-sm">{t.name}</span>
                  <span className="block text-[#52525B] text-xs font-body">{t.grade}</span>
                </div>
                <span className="text-[#F2C402] font-black text-lg font-sans">{t.score}</span>
              </div>
            </motion.div>
          ))}
        </div>
      </div>
    </section>

    {/* FAQ */}
    <section data-testid="best-faq" className="py-20 md:py-28 bg-[#FAFAFA]">
      <div className="container mx-auto px-6 lg:px-16 max-w-3xl">
        <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} className="text-center mb-16">
          <h2 className="text-3xl md:text-4xl font-black text-[#121212] tracking-tight">
            FAQs About <span className="text-[#F2C402]">CBSE Coaching in Santacruz</span>
          </h2>
        </motion.div>
        <Accordion type="single" collapsible className="space-y-3" data-testid="best-faq-accordion">
          {faqs.map((faq, i) => (
            <AccordionItem key={i} value={`faq-${i}`} data-testid={`best-faq-item-${i}`}
              className="bg-white rounded-xl border-2 border-[#121212] px-6 overflow-hidden">
              <AccordionTrigger className="text-[#121212] font-sans font-bold text-sm md:text-base hover:no-underline py-5">
                {faq.q}
              </AccordionTrigger>
              <AccordionContent className="text-[#52525B] font-body text-sm leading-relaxed">
                {faq.a}
              </AccordionContent>
            </AccordionItem>
          ))}
        </Accordion>
      </div>
    </section>

    {/* CTA + FORM */}
    <section data-testid="best-cta" className="py-16 md:py-24 bg-[#F2C402] border-y-2 border-[#121212]">
      <div className="container mx-auto px-6 lg:px-16">
        <div className="grid lg:grid-cols-2 gap-12 items-center">
          <div className="text-center lg:text-left">
            <h2 className="text-3xl md:text-4xl lg:text-5xl font-black text-[#0A0A0A] tracking-tight">
              Experience the Best CBSE Coaching in Santacruz
            </h2>
            <p className="text-[#0A0A0A]/70 text-base md:text-lg mt-4 font-body max-w-lg">
              Join 500+ successful students. Book your free demo class today.
            </p>
            <div className="flex flex-wrap gap-4 mt-8 justify-center lg:justify-start">
              <a href={WA} target="_blank" rel="noopener noreferrer" data-testid="best-cta-whatsapp"
                className="flex items-center gap-2 bg-[#0A0A0A] text-white px-8 py-4 rounded-xl font-bold text-sm hover:bg-[#121212] transition-all">
                <WaIcon /> WhatsApp Us
              </a>
              <a href={TEL} data-testid="best-cta-call"
                className="flex items-center gap-2 bg-white text-[#0A0A0A] px-8 py-4 rounded-xl font-bold text-sm border-2 border-[#121212] shadow-[4px_4px_0px_0px_rgba(0,0,0,1)]">
                <Phone className="w-4 h-4" /> Call Now
              </a>
            </div>
            <div className="flex items-center gap-2 mt-6 text-[#0A0A0A]/60 text-sm font-body justify-center lg:justify-start">
              <MapPin className="w-4 h-4" />
              <span>Tirupati Shopping Center, Swami Vivekanand Rd, Santacruz West</span>
            </div>
            <Link to="/" data-testid="best-back-home" className="inline-flex items-center gap-1 text-[#0A0A0A]/60 text-sm font-body mt-4 hover:text-[#0A0A0A] transition-colors">
              <ArrowRight className="w-4 h-4 rotate-180" /> Back to Home
            </Link>
          </div>
          <div className="lg:block hidden"><LeadForm /></div>
        </div>
        <div className="lg:hidden mt-10"><LeadForm /></div>
      </div>
    </section>
  </>
);

export default BestCBSECoaching;
